import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../../components/tailwind-component/ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Plus, Pencil, Trash2, FileText, Files, Layers, Zap, MousePointerClick, UserPlus, Link2, Check, X } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { Methods } from '../../../../../core/utility';
import { ExistingIgAutomation, ZapTopTabsType } from 'custom-type';
import { NavLink } from "react-router-dom";
import { useState } from 'react';
import { toast } from 'react-toastify';
import { InstagramDMAutomation } from "../../../../../core/services/dm-automation-instagram.service";

const automationService = new InstagramDMAutomation();

const methods: Methods = new Methods()
interface AutomationListProps {
  automations: ExistingIgAutomation[];
  onEdit: (automation: ExistingIgAutomation) => void;
  // handleOpenConnectInstagramModal: () => void;
  handleDeleteAutomation: (id: number, name: string) => void;
  handleToggleAutomation: (id: number, status: boolean, getActiveAutomations?: boolean) => void;
  // new props for pagination loading
  isLoading: boolean;
  hasMore: boolean;
  loadMoreRef: any;
  setTopTab: (type: ZapTopTabsType) => void;
  showActiveOnly: boolean;
  setShowActiveOnly: (type: boolean) => void;
  /** Whose Zaps these are — the rename is scoped to them server-side. */
  influencerId?: number | string;
}

export function AutomationList({
  automations,
  onEdit,
  // handleOpenConnectInstagramModal,
  handleDeleteAutomation,
  handleToggleAutomation,
  isLoading,
  hasMore,
  loadMoreRef,
  setTopTab,
  showActiveOnly,
  setShowActiveOnly,
  influencerId,
}: AutomationListProps) {

  /* Renaming in place. Zaps built from a template are named after the goal and
     the keyword, so a creator with two "Share my link · LINK" Zaps cannot tell
     them apart in this list — and the name was only editable inside the wizard,
     behind Update Automation. */
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [savingRename, setSavingRename] = useState(false);
  // Renamed rows, echoed locally: the list is paginated, and refetching it
  // would drop the creator back to page one for a rename.
  const [renamed, setRenamed] = useState<Record<number, string>>({});

  const nameOf = (automation: any): string =>
    renamed[automation.ig_automation_id] ?? automation.automation_name;

  const startRename = (automation: any) => {
    setRenamingId(automation.ig_automation_id);
    setRenameDraft(nameOf(automation) || '');
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameDraft('');
  };

  const saveRename = async (automation: any) => {
    const name = renameDraft.trim();
    if (!name || savingRename) return;
    if (name === (nameOf(automation) || '').trim()) { cancelRename(); return; }

    setSavingRename(true);
    try {
      const res: any = await automationService.igRenameAutomation({
        ig_automation_id: automation.ig_automation_id,
        influencer_id: influencerId ?? automation.influencer_id,
        automation_name: name,
      });
      if (res?.status === 1) {
        automation.automation_name = name;
        setRenamed((prev) => ({ ...prev, [automation.ig_automation_id]: name }));
        cancelRename();
      } else {
        toast.error(res?.info || 'Could not rename this Zap');
      }
    } catch {
      toast.error('Could not rename this Zap');
    } finally {
      setSavingRename(false);
    }
  };

  /**
   * One badge summarising how this zap's DM goes out, so a creator can tell an
   * "opener card" zap from a "sends immediately" one without opening it.
   *
   * An automation holds several rules and they can disagree, so we show the
   * STRONGEST signal present — a card whose badge says "Follow required" is
   * telling the truth about at least one of its rules, which is the thing worth
   * knowing at a glance. 'instant' shows nothing: it is the default, and a badge
   * on every card would carry no information.
   */
  const getDmFlowBadge = (automation: ExistingIgAutomation) => {
    const modes = automation.automation_rules_data?.map((r) => r.dm_flow_mode) || [];
    if (modes.includes('hard_follow')) {
      return { label: 'Follow required', Icon: UserPlus };
    }
    if (modes.includes('soft_follow')) {
      return { label: 'Asks to follow', Icon: UserPlus };
    }
    if (modes.includes('button')) {
      return { label: 'Button first', Icon: MousePointerClick };
    }
    return null;
  };

  /** true when any rule delivers its DM as a card with a tappable link button. */
  const hasLinkButton = (automation: ExistingIgAutomation) =>
    automation.automation_rules_data?.some((r) => !!r.button_url) || false;

  const getScopeIcon = (scope: string | null ) => {
    switch (scope) {
      case 'single':
        return FileText;
      case 'multiple':
        return Files;
      case 'all':
        return Layers;
      default:
        return FileText;
    }
  };

  const getScopeLabel = (scope: string | null, mediaType: string | null) => {
    if (mediaType === 'story') {
      switch (scope) {
        case 'single':
          return 'Single Story';
        case 'multiple':
          // return 'Multiple Stories';
          return 'Stories';
        case 'all':
          return 'All Stories';
        default:
          return scope;
      }
    } 
    else if (mediaType === 'live'){
      return 'Insta Live'
    }
    else {
      switch (scope) {
        case 'single':
          return 'Single Post';
        case 'multiple':
          // return 'Multiple Posts';
          return 'Posts';
        case 'all':
          return 'All Posts';
        default:
          return scope;
      }
    }
  };

  const AutomationSkeleton = () => {
    return (
      <Card className="border animate-pulse">
        <CardContent className="p-3 md:p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1 space-y-3">
              {/* Title + status */}
              <div className="flex items-center gap-3">
                <div className="h-4 w-40 bg-gray-200 rounded" />
                <div className="h-5 w-16 bg-gray-200 rounded-full" />
              </div>

              {/* Posts preview */}
              <div className="flex gap-2">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 bg-gray-100 border rounded-lg p-2"
                  >
                    <div className="w-10 h-10 bg-gray-200 rounded" />
                    <div className="h-3 w-24 bg-gray-200 rounded" />
                  </div>
                ))}
              </div>

              {/* Keywords */}
              <div className="space-y-1">
                <div className="h-3 w-20 bg-gray-200 rounded" />
                <div className="flex gap-2 flex-wrap">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="h-5 w-14 bg-gray-200 rounded-full" />
                  ))}
                </div>
              </div>

              {/* Meta */}
              <div className="flex gap-4">
                <div className="h-3 w-24 bg-gray-200 rounded" />
                <div className="h-3 w-20 bg-gray-200 rounded" />
                <div className="h-3 w-16 bg-gray-200 rounded" />
              </div>

              {/* Footer */}
              <div className="flex justify-between items-center">
                <div className="h-3 w-32 bg-gray-200 rounded" />
                <div className="flex gap-2">
                  <div className="h-8 w-8 bg-gray-200 rounded" />
                  <div className="h-8 w-8 bg-gray-200 rounded" />
                </div>
              </div>
            </div>

            {/* Toggle */}
            <div className="h-5 w-10 bg-gray-200 rounded-full ml-4" />
          </div>
        </CardContent>
      </Card>
    );
  };

  /** Load on tab open */

  return (
    <div className="space-y-6">
      <Card className='p-0 border-0 bg-transparent' >
        <CardHeader className='p-0'>
          <div className="flex flex-row md:items-center justify-between">
            {/* <div className='mb-2'>
              <CardTitle>Your Automations</CardTitle>
              <CardDescription>
                Manage your Instagram comment and DM automation workflows
              </CardDescription>
            </div> */}
            <div className="flex items-center justify-between">
              {/* <CardTitle>Your Automations</CardTitle> */}

              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showActiveOnly}
                  onChange={(e) => setShowActiveOnly(e.target.checked)}
                  className="h-4 w-4 accent-purple-600"
                />
                Show active Zaps only
              </label>
            </div>
            <span className='text-[14px] text-gray-600'>{automations.length} {showActiveOnly ? "active Zaps" : "Zaps" }</span>
            {/* <Button 
              onClick={() => setTopTab('create')}
              className='btn btn-primary' 
              variant="outline"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Zap
            </Button> */}
            {/* <NavLink
              to="/influencer/zap/automations"
              className="btn btn-primary btn-outline inline-flex items-center"
              style={{ textDecoration: "none" }}
            >
              <Plus className="w-4 h-4 mr-2" />
              New Automation
            </NavLink> */}
          </div>
        </CardHeader>
        <CardContent className='px-0'>
          <div className="space-y-4">
            {automations.length === 0 && isLoading && (
              [...Array(4)].map((_, i) => <AutomationSkeleton key={i} />)
            )}
            {
              automations.length === 0 
              ? 
              (
                <div className="text-center py-12 text-gray-500">
                  <Zap className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No automations yet</p>
                  <p className="text-sm mb-4">Create your first Zap to get started</p>
                  <Button 
                    // onClick={handleOpenConnectInstagramModal}
                    onClick={() => setTopTab('create')}
                    variant="outline"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Zap
                  </Button>
                  {/* <NavLink
                    to="/influencer/zap/automations"
                    className="inline-flex items-center"
                    style={{ textDecoration: "none" }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Automation
                  </NavLink> */}
                </div>
              ) 
              : 
              (
                automations.map((automation: ExistingIgAutomation) => {
                  const ScopeIcon = getScopeIcon(automation?.scope);
                  const dmFlowBadge = getDmFlowBadge(automation);
                  const showsLinkButton = hasLinkButton(automation);

                  return (
                    <Card key={automation.ig_automation_id} className="border-1">
                      <CardContent className="p-3 md:p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 gap-2 flex flex-column">
                            <div className="flex gap-3 mb-2 me-auto flex-wrap w-full">
                              <div className='d-flex gap-2 w-full md:w-auto'>
                                <div className='flex gap-2 md:flex-row flex-col  me-auto'>
                                  {renamingId === automation.ig_automation_id ? (
                                    <div className="flex items-center gap-2 me-auto">
                                      <input
                                        value={renameDraft}
                                        autoFocus
                                        maxLength={255}
                                        placeholder="Name this Zap"
                                        onChange={(e) => setRenameDraft(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') { e.preventDefault(); saveRename(automation); }
                                          if (e.key === 'Escape') cancelRename();
                                        }}
                                        style={{
                                          border: '1px solid #7c3aed', borderRadius: 8,
                                          padding: '6px 10px', fontSize: '1rem', fontWeight: 600, minWidth: 200,
                                        }}
                                      />
                                      <Check
                                        className={`w-4 h-4 ${savingRename || !renameDraft.trim() ? 'text-gray-300' : 'text-green-600 cursor-pointer'}`}
                                        onClick={() => saveRename(automation)}
                                      />
                                      <X className="w-4 h-4 cursor-pointer text-gray-400" onClick={cancelRename} />
                                    </div>
                                  ) : (
                                    <h3
                                      // Inline, not flex: the pencil should follow the
                                      // last word of a wrapped name, not hover beside
                                      // the first line.
                                      className='me-auto'
                                    >
                                      {nameOf(automation)}
                                      <Pencil
                                        className="ml-2 inline h-3.5 w-3.5 cursor-pointer align-baseline text-gray-400 hover:text-[#7c3aed]"
                                        onClick={() => startRename(automation)}
                                        aria-label="Rename this Zap"
                                      />
                                    </h3>
                                  )}
                                  {automation.automation_rules_data?.some(r => r.rule_type === 'zap_link') && (
                                  <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full self-start" style={{ background: 'linear-gradient(135deg, #9C52A2 0%, #e1306c 50%, #FA8E41 100%)', color: '#fff' }}>
                                    <Zap className="w-3 h-3 fill-white" />
                                    Zap Link
                                  </span>
                                )} 

                                {/* How the DM goes out. Muted on purpose — the loud
                                    gradient above is reserved for Zap Link, and these
                                    are a property of the zap rather than its headline. */}
                                {dmFlowBadge && (
                                  <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full self-start bg-[#f3f0ff] text-[#7c3aed]">
                                    <dmFlowBadge.Icon className="w-3 h-3" />
                                    {dmFlowBadge.label}
                                  </span>
                                )}

                                {showsLinkButton && (
                                  <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full self-start bg-gray-100 text-gray-600">
                                    <Link2 className="w-3 h-3" />
                                    Link button
                                  </span>
                                )}
                                
                                </div>
                                  <Badge variant={automation.is_active ? 'outline' : 'secondary'} className={`btn btn-primary z-0 ms-auto align-self-start ${automation.is_active ? "btn btn-blue" : "btn btn-outline-primary"}` }>
                                    {automation.is_active ? 'Active' : 'Paused'}
                                  </Badge>
                                  <Switch
                                    className='ms-auto hidden md:flex'
                                    checked={automation.is_active}
                                    onCheckedChange={() => handleToggleAutomation(automation.ig_automation_id, !automation.is_active, showActiveOnly)}
                                  />
                                </div>
                               
                                
                              </div>
                            
                            {automation.automation_posts_data?.length > 0 && automation.scope !== 'all' && (
                              <div className='flex gap-2'>
                                {automation.automation_posts_data.slice(0, 3)?.map((post) => (
                                  <div
                                    key={post.shortcode}
                                    className="flex items-center gap-2 bg-gray-50 border rounded-lg p-2"
                                  >
                                    <ImageWithFallback
                                      src={post.gcp_media_file_url}
                                      alt="Post thumbnail"
                                      className="w-10 h-10 object-cover rounded"
                                    />
                                    <p className="text-xs line-clamp-1 max-w-[150px]">
                                      {post.caption}
                                    </p>
                                  </div>
                                
                                ))}
                                {automation.automation_posts_data.length > 3 && (
                                  <div className="flex items-center justify-center bg-gray-100 border rounded-lg px-3 py-2">
                                    <span className="text-xs text-gray-600">
                                      +{automation.automation_posts_data.length - 3} more
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}

                            <div className=''> 
                              <p className="text-xs text-gray-500 pb-1">Keywords</p>
                              <div className="flex gap-2 flex-wrap">
                                {automation.automation_rules_data?.map((rule) => (
                                  <div key={rule.ig_automation_rule_id} className="flex gap-1 flex-wrap">
                                    {rule.keywords.map((kw) => (
                                      <>
                                      <button key={kw} className={rule.is_active ? 'btn btn-blue btn-xs btn-xs fs-12' : 'btn btn-outline btn-xs btn-xs fs-12'}>
                                        {kw}
                                      </button>
                                      {/* <Badge
                                        key={kw}
                                        variant={rule.is_active ? 'default' : 'outline'}
                                        className={rule.is_active ? 'btn btn-primary' : 'btn btn-outline'}
                                        
                                      >
                                        {kw}
                                      </Badge> */}
                                      </>
                                    ))}
                                  </div>
                                ))}
                              </div>
                              
                            </div>
                            {/* <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                              <span>{automation.trigger_count} triggers 
                                <span className='fs-12 ps-1' style={{color:'#999'}}>( today - {automation.daily_interaction_utilized} )</span>
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                              <ScopeIcon className="w-4 h-4" />
                              <span>{getScopeLabel(automation.scope, automation.automation_media_type)}</span>
                              <span className="text-gray-400">•</span>
                              <span>{automation.automation_rules_data?.length} rules</span>
                              <span className="text-gray-400">•</span>
                            </div> */}
                            {/* Desktop View */}
                            <div className="hidden md:flex items-center gap-2 text-sm text-gray-600 mb-1">
                              <ScopeIcon className="w-4 h-4" />
                              <span>{getScopeLabel(automation.scope, automation.automation_media_type)}</span>
                              <span className="text-gray-400">•</span>
                              <span>{automation.automation_rules_data?.length} rules</span>
                              <span className="text-gray-400">•</span>
                              <span>
                                {automation.trigger_count} triggers
                                <span className="fs-12 ps-1" style={{ color: "#999" }}>
                                  ( today - {automation.daily_interaction_utilized} )
                                </span>
                              </span>
                            </div>

                            {/* Mobile View */}
                            <div className="md:hidden">
                              <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                                <span>
                                  {automation.trigger_count} triggers
                                  <span className="fs-12 ps-1" style={{ color: "#999" }}>
                                    ( today - {automation.daily_interaction_utilized} )
                                  </span>
                                </span>
                              </div>

                              <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                                <ScopeIcon className="w-4 h-4" />
                                <span>{getScopeLabel(automation.scope, automation.automation_media_type)}</span>
                                <span className="text-gray-400">•</span>
                                <span>{automation.automation_rules_data?.length} rules</span>
                              </div>
                            </div>
                            <div className='flex justify-between items-center border-t'>
                              <p className="text-xs text-gray-500">
                                Created {methods.formatTimeAgo(automation.created_at)}
                              </p>
                              <div className=''>
                                <Switch
                                className='md:hidden'
                                checked={automation.is_active}
                                onCheckedChange={() => handleToggleAutomation(automation.ig_automation_id, !automation.is_active, showActiveOnly)}
                              />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className='p-2'
                                  onClick={() => onEdit(automation)}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className='p-2'
                                  onClick={() => handleDeleteAutomation(automation.ig_automation_id, automation.automation_name)}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )
            }

            {hasMore && isLoading && automations.length > 0 && (
              [...Array(3)].map((_, i) => <AutomationSkeleton key={`more-${i}`} />)
            )}
            <>
              {hasMore && (
                <div
                  ref={loadMoreRef}
                  className="flex justify-center py-4 text-sm text-gray-400"
                >
                  {isLoading ? "Loading more activities..." : "Scroll to load more"}
                </div>
              )}
            </>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}